import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/library/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import { child_spesific_key } from "@/config/endpoints";
import { getCustomFieldValue } from "@/utils/getFromCustom";
import {
  useVideoChildModuleData,
  getVideoModuleEntry,
} from "@/module/videoProduction/helpers/useVideoChildModuleData";
import useVariants from "@/features/videoProduction/api/useVariants";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import {
  Video,
  IndianRupee,
  Layers,
  Users,
  TrendingUp,
  Clock,
  Wallet,
  User,
  Tag,
} from "lucide-react";
import { useMemo } from "react";
import { usePermissions } from "@/rbac/usePermissions";
import { filterCustomInputFieldsByViewPermission } from "@/rbac/customFieldValueAccess";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";

const fmt = (v: number) =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 0 });

function DetailItem({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex flex-col min-w-0">
      <p className="text-[12px] text-[#8a918d] tracking-[-0.22px] truncate">
        {label}
      </p>
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="text-[14px] font-semibold text-[#051912] tracking-[-0.28px] truncate">
            {value || "N/A"}
          </p>
        </TooltipTrigger>
        <TooltipContent className="bg-slate-900 text-white text-xs border-0">
          {value || "N/A"}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

const PAYMENT_STATUS: Record<
  string,
  { label: string; bg: string; text: string }
> = {
  advance: { label: "Advance", bg: "#e0f2fe", text: "#0369a1" },
  partial: { label: "Partial", bg: "#fef3c7", text: "#92400e" },
  full: { label: "Paid", bg: "#dcfce7", text: "#166534" },
  pending: { label: "Pending", bg: "#fee2e2", text: "#991b1b" },
};

export default function VideoServiceVariantWidget() {
  const { child, isChildActive } = useActiveDataMutation();
  const { allChildren } = useChildForm();
  const { serviceName, variantName, variantId } =
    useVideoChildModuleData(child);
  const { variants } = useVariants();
  const { pipeline } = useLeadPipeline();

  const { spesificFields } = useSpesificCustoms({
    queryKey: child_spesific_key,
    modelName: "child",
    stageId: child?.stage?.id,
    isActive: isChildActive,
  });

  const { canGlobal, getRawGlobalModule } = usePermissions();
  // Pass the raw reader too: without it the per-field gate cannot tell a
  // field the role simply never configured from one it explicitly denied.
  const cfPerms = { canGlobal, getRawGlobalModule };

  const variant = useMemo(
    () => variants?.find((v: any) => v.id === variantId),
    [variants, variantId]
  );

  const selectedDealPrice = useMemo(() => {
    if (!variant) return 0;
    return (variant.price ?? 0) + (variant.costAllowed ?? 0);
  }, [variant]);

  const allDealsInsights = useMemo(() => {
    if (!allChildren?.length || !variants?.length) {
      return {
        totalValue: 0,
        totalDeals: 0,
        byVariant: [] as any[],
        byStage: [] as any[],
      };
    }

    const variantCountMap = new Map<number, number>();
    const stageMap = new Map<
      number,
      { name: string; count: number; value: number }
    >();

    for (const c of allChildren) {
      const entry = getVideoModuleEntry(c);
      const vId = entry?.id;
      if (vId != null) {
        variantCountMap.set(vId, (variantCountMap.get(vId) ?? 0) + 1);
      }

      const stageId = c.stageId ?? c.stage?.id;
      const stageName =
        c.stage?.name ??
        pipeline?.find((s: any) => s.id === stageId)?.name ??
        "Unknown";
      if (stageId) {
        const existing = stageMap.get(stageId) ?? {
          name: stageName,
          count: 0,
          value: 0,
        };
        existing.count += 1;
        if (vId != null) {
          const v = variants.find((x: any) => x.id === vId);
          if (v) existing.value += (v.price ?? 0) + (v.costAllowed ?? 0);
        }
        stageMap.set(stageId, existing);
      }
    }

    let totalValue = 0;
    const byVariant: { name: string; count: number; subtotal: number }[] = [];
    for (const [vId, count] of variantCountMap) {
      const v = variants.find((x: any) => x.id === vId);
      if (!v) continue;
      const unit = (v.price ?? 0) + (v.costAllowed ?? 0);
      const subtotal = unit * count;
      totalValue += subtotal;
      byVariant.push({ name: v.name ?? "Unknown", count, subtotal });
    }

    const stageOrder = pipeline?.map((s: any) => s.id) ?? [];
    const byStage = Array.from(stageMap.entries())
      .sort((a, b) => stageOrder.indexOf(a[0]) - stageOrder.indexOf(b[0]))
      .map(([, data]) => data);

    return { totalValue, totalDeals: allChildren.length, byVariant, byStage };
  }, [allChildren, variants, pipeline]);

  const paymentInsights = useMemo(() => {
    if (!allChildren?.length) return null;
    let totalPaid = 0;
    let totalPending = 0;
    let totalAdvance = 0;
    let totalNet = 0;
    let totalDiscount = 0;

    const perDeal: {
      name: string;
      paid: number;
      net: number;
      pending: number;
      status: string;
      owner: string;
    }[] = [];

    const byOwner = new Map<
      string,
      { paid: number; pending: number; count: number }
    >();

    for (const c of allChildren) {
      const net = c.netAmount ?? 0;
      const paid = c.totalPaid ?? 0;
      const pend = c.pendingAmount ?? 0;
      totalPaid += paid;
      totalPending += pend;
      totalAdvance += c.advanceAmount ?? 0;
      totalNet += net;
      totalDiscount += c.discountAmount ?? 0;

      const ownerUser = c.childUsers?.[0]?.user;
      const ownerName = ownerUser?.name ?? "Unassigned";

      perDeal.push({
        name: c.name ?? `Deal ${c.id}`,
        paid,
        net,
        pending: pend,
        status: c.paymentStatus ?? "pending",
        owner: ownerName,
      });

      const existing = byOwner.get(ownerName) ?? {
        paid: 0,
        pending: 0,
        count: 0,
      };
      existing.paid += paid;
      existing.pending += pend;
      existing.count += 1;
      byOwner.set(ownerName, existing);
    }

    return {
      totalPaid,
      totalPending,
      totalAdvance,
      totalNet,
      totalDiscount,
      perDeal,
      byOwner: Array.from(byOwner.entries()).map(([name, data]) => ({
        name,
        ...data,
      })),
    };
  }, [allChildren]);

  const customScopes = child?.customInputScopes ?? [];
  const customFieldEntries = useMemo(() => {
    if (!spesificFields?.length || !customScopes?.length) return [];
    const visible = filterCustomInputFieldsByViewPermission(
      spesificFields,
      cfPerms
    );
    return visible
      .map((field: any) => ({
        label: getCfDisplayName(field),
        value: getCustomFieldValue(field, customScopes),
      }))
      .filter((e: any) => e.value && e.value !== "N/A");
  }, [spesificFields, customScopes, canGlobal]);

  if (!isChildActive || !child?.modules) return null;

  const paidPercent =
    paymentInsights && paymentInsights.totalNet > 0
      ? (paymentInsights.totalPaid / paymentInsights.totalNet) * 100
      : 0;

  return (
    <Card className="w-full gap-0 border border-[#e6e8e7] shadow-none rounded-[14px] bg-white overflow-hidden py-0">
      <CardHeader className="px-4 py-3 pb-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-[16px] font-semibold text-[#051912] tracking-[-0.32px] leading-normal">
            Video Service Variant
          </CardTitle>
          {selectedDealPrice > 0 && (
            <span className="text-[16px] font-bold text-[#051912] tabular-nums">
              ₹{fmt(selectedDealPrice)}
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0 pb-2">
        <div className="flex flex-col">
          {/* Selected deal details */}
          <div className="px-4 py-2.5">
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              {serviceName && (
                <div className="flex items-center gap-2 min-w-0">
                  <Video className="size-4 text-[#697571] shrink-0" />
                  <DetailItem label="Service" value={serviceName} />
                </div>
              )}
              <div className="flex items-center gap-2 min-w-0">
                <Layers className="size-4 text-[#697571] shrink-0" />
                <DetailItem label="Variant" value={variantName} />
              </div>
              {variant && (
                <div className="flex items-center gap-2 min-w-0">
                  <IndianRupee className="size-4 text-[#267355] shrink-0" />
                  <DetailItem
                    label="Unit Price"
                    value={`₹${fmt(selectedDealPrice)}`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Custom fields */}
          {customFieldEntries.length > 0 && (
            <div className="px-4 py-2 border-t border-[#f0f2f1]">
              <div className="flex flex-wrap gap-x-8 gap-y-2">
                {customFieldEntries.map((entry: any, i: number) => (
                  <DetailItem key={i} label={entry.label} value={entry.value} />
                ))}
              </div>
            </div>
          )}

          {/* All deals breakdown */}
          {allDealsInsights.totalDeals > 0 && (
            <div className="px-4 py-3 border-t border-[#e6e8e7]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-semibold text-[#8a918d] uppercase tracking-[0.4px]">
                  All Deals
                </span>
                <div className="flex items-center gap-1.5">
                  <Users className="size-3.5 text-[#697571]" />
                  <span className="text-[13px] font-medium text-[#697571]">
                    {allDealsInsights.totalDeals} deal
                    {allDealsInsights.totalDeals > 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                {allDealsInsights.byVariant.map((bv, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-[13px] text-[#374741] truncate mr-3">
                      {bv.name}
                      <span className="text-[#9ba3a0] ml-1.5 text-[12px]">
                        ×{bv.count}
                      </span>
                    </span>
                    <span className="text-[14px] text-[#051912] font-semibold tabular-nums shrink-0">
                      ₹{fmt(bv.subtotal)}
                    </span>
                  </div>
                ))}
              </div>

              {allDealsInsights.byStage.length > 1 && (
                <div className="mt-2.5 pt-2 border-t border-[#f0f2f1]">
                  <span className="text-[11px] font-semibold text-[#9ba3a0] uppercase tracking-[0.4px]">
                    By Stage
                  </span>
                  <div className="flex flex-col gap-1 mt-1">
                    {allDealsInsights.byStage.map((bs, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between"
                      >
                        <span className="text-[13px] text-[#697571] truncate mr-3">
                          {bs.name}
                          <span className="text-[#9ba3a0] ml-1.5 text-[12px]">
                            ({bs.count})
                          </span>
                        </span>
                        <span className="text-[13px] text-[#374741] font-medium tabular-nums shrink-0">
                          ₹{fmt(bs.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-[#e6e8e7]">
                <span className="text-[14px] font-bold text-[#051912]">
                  Total Deal Value
                </span>
                <span className="text-[16px] font-bold text-[#267355] tabular-nums">
                  ₹{fmt(allDealsInsights.totalValue)}
                </span>
              </div>
            </div>
          )}

          {/* Payment Summary */}
          {paymentInsights && paymentInsights.totalNet > 0 && (
            <div className="px-4 py-3 border-t border-[#e6e8e7]">
              <span className="text-[12px] font-semibold text-[#8a918d] uppercase tracking-[0.4px]">
                Payment Summary
              </span>

              {/* Key metrics */}
              <div className="grid grid-cols-5 gap-2 mt-2.5">
                <MetricBox
                  label="Net Value"
                  value={fmt(paymentInsights.totalNet)}
                  icon={<IndianRupee className="size-3" />}
                  color="#46a580"
                />
                <MetricBox
                  label="Discount"
                  value={fmt(paymentInsights.totalDiscount)}
                  icon={<Tag className="size-3" />}
                  color="#8b5cf6"
                />
                <MetricBox
                  label="Received"
                  value={fmt(paymentInsights.totalPaid)}
                  icon={<TrendingUp className="size-3" />}
                  color="#2d7a5a"
                />
                <MetricBox
                  label="Pending"
                  value={fmt(paymentInsights.totalPending)}
                  icon={<Clock className="size-3" />}
                  color="#c77800"
                />
                <MetricBox
                  label="Advance"
                  value={fmt(paymentInsights.totalAdvance)}
                  icon={<Wallet className="size-3" />}
                  color="#4a7cc9"
                />
              </div>

              {/* Main progress bar */}
              <div className="mt-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12px] text-[#697571]">
                    Collection Progress
                  </span>
                  <span className="text-[12px] font-bold text-[#2d7a5a] tabular-nums">
                    {Math.round(paidPercent)}%
                  </span>
                </div>
                <div className="h-[6px] rounded-full bg-[#e8eae9] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#46a580] to-[#2d7a5a] transition-all duration-500"
                    style={{ width: `${Math.min(paidPercent, 100)}%` }}
                  />
                </div>
              </div>

              {/* Per-deal breakdown */}
              {paymentInsights.perDeal.length > 0 && (
                <div className="mt-3 pt-2.5 border-t border-[#f0f2f1]">
                  <span className="text-[11px] font-semibold text-[#9ba3a0] uppercase tracking-[0.4px]">
                    Per Deal
                  </span>
                  <div className="flex flex-col gap-2 mt-1.5">
                    {paymentInsights.perDeal.map((d, i) => {
                      const dealPct =
                        d.net > 0 ? Math.min((d.paid / d.net) * 100, 100) : 0;
                      const statusCfg =
                        PAYMENT_STATUS[d.status] || PAYMENT_STATUS.pending;
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-0.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-[13px] font-medium text-[#374741] truncate">
                                {d.name}
                              </span>
                              <span
                                className="text-[9px] font-bold px-1.5 py-[1px] rounded-full shrink-0"
                                style={{
                                  backgroundColor: statusCfg.bg,
                                  color: statusCfg.text,
                                }}
                              >
                                {statusCfg.label}
                              </span>
                            </div>
                            <span className="text-[13px] font-semibold text-[#1e302a] tabular-nums shrink-0 ml-2">
                              {fmt(d.paid)}/{fmt(d.net)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-[4px] rounded-full bg-[#e8eae9] overflow-hidden">
                              <div
                                className="h-full rounded-full bg-[#46a580] transition-all duration-300"
                                style={{ width: `${dealPct}%` }}
                              />
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <User className="size-3 text-[#9ba3a0]" />
                              <span className="text-[11px] text-[#8a918d]">
                                {d.owner}
                              </span>
                            </div>
                          </div>
                          {d.pending > 0 && (
                            <span className="text-[11px] text-[#c77800] font-medium mt-0.5 inline-block">
                              ₹{fmt(d.pending)} pending
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* By owner */}
              {paymentInsights.byOwner.length > 1 && (
                <div className="mt-3 pt-2.5 border-t border-[#f0f2f1]">
                  <span className="text-[11px] font-semibold text-[#9ba3a0] uppercase tracking-[0.4px]">
                    By Owner
                  </span>
                  <div className="flex flex-col gap-1.5 mt-1.5">
                    {paymentInsights.byOwner.map((o, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <User className="size-3.5 text-[#697571] shrink-0" />
                          <span className="text-[13px] text-[#374741] truncate">
                            {o.name}
                            <span className="text-[#9ba3a0] ml-1 text-[11px]">
                              ({o.count} deal{o.count > 1 ? "s" : ""})
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[12px] text-[#2d7a5a] font-semibold tabular-nums">
                            ₹{fmt(o.paid)}
                          </span>
                          {o.pending > 0 && (
                            <span className="text-[12px] text-[#c77800] font-medium tabular-nums">
                              ₹{fmt(o.pending)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MetricBox({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 rounded-[8px] bg-[#f7f9f8] border border-[#e6e8e7] px-2.5 py-2">
      <div className="flex items-center gap-1" style={{ color }}>
        {icon}
        <span className="text-[9px] font-semibold uppercase tracking-[0.3px]">
          {label}
        </span>
      </div>
      <span className="text-[14px] font-bold text-[#1e302a] tabular-nums leading-tight">
        {value}
      </span>
    </div>
  );
}
